import { apiRequest } from './client'

export type PriceMoveDto = {
  catalog_item_id: number
  organization_id: number
  product_type_id: number | null
  unit: string | null
  previous_price: number
  current_price: number
  delta_abs: number
  delta_pct: number | null
  observed_at: string
  is_private: boolean
  previous_observed_at?: string | null
  private_supplier_id?: number | null
  supplier_name?: string | null
  item_name?: string | null
}

export type PriceHistoryPointDto = {
  observed_at: string
  price: number
  source: string | null
}

export type PriceMoveDirection = 'all' | 'down' | 'up'

export type FetchPriceMovesOptions = {
  limit?: number
  direction?: PriceMoveDirection
  privateSupplierId?: number | null
}

type Raw = Record<string, unknown>

const readNumber = (json: Raw, ...keys: string[]): number => {
  for (const key of keys) {
    const value = json[key]
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
  }
  return 0
}

const readOptionalNumber = (json: Raw, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = json[key]
    if (value == null) {
      continue
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
  }
  return null
}

const readString = (json: Raw, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = json[key]
    if (typeof value === 'string' && value.trim()) {
      return value
    }
  }
  return null
}

const readInstant = (json: Raw, ...keys: string[]): string => {
  for (const key of keys) {
    const value = json[key]
    if (typeof value === 'string' && value.trim()) {
      return value
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      // epoch seconds vs millis
      const ms = value < 1e12 ? value * 1000 : value
      const date = new Date(ms)
      if (!Number.isNaN(date.getTime())) {
        return date.toISOString()
      }
    }
    if (Array.isArray(value) && value.length >= 3) {
      const [year, month, day, hour = 0, minute = 0, second = 0] = value as number[]
      const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
      if (!Number.isNaN(date.getTime())) {
        return date.toISOString()
      }
    }
  }
  return ''
}

const normalizePriceMove = (json: Raw): PriceMoveDto => ({
  catalog_item_id: readNumber(json, 'catalog_item_id', 'catalogItemId'),
  organization_id: readNumber(json, 'organization_id', 'organizationId'),
  product_type_id: readOptionalNumber(json, 'product_type_id', 'productTypeId'),
  unit: readString(json, 'unit'),
  previous_price: readNumber(json, 'previous_price', 'previousPrice'),
  current_price: readNumber(json, 'current_price', 'currentPrice'),
  delta_abs: readNumber(json, 'delta_abs', 'deltaAbs'),
  delta_pct: readOptionalNumber(json, 'delta_pct', 'deltaPct'),
  observed_at: readInstant(json, 'observed_at', 'observedAt'),
  is_private: Boolean(json.is_private ?? json.isPrivate ?? json.privatePrice ?? false),
  previous_observed_at: readInstant(json, 'previous_observed_at', 'previousObservedAt') || null,
  private_supplier_id: readOptionalNumber(json, 'private_supplier_id', 'privateSupplierId'),
  supplier_name: readString(json, 'supplier_name', 'supplierName'),
  item_name: readString(json, 'item_name', 'itemName'),
})

const normalizeHistoryPoint = (json: Raw): PriceHistoryPointDto => ({
  observed_at: readInstant(json, 'observed_at', 'observedAt'),
  price: readNumber(json, 'price'),
  source: readString(json, 'source'),
})

export const fetchPriceMoves = async (
  organizationId: number,
  options: FetchPriceMovesOptions | number = {},
): Promise<PriceMoveDto[]> => {
  const normalized: FetchPriceMovesOptions =
    typeof options === 'number' ? { limit: options } : options
  const limit = normalized.limit ?? 50
  const params = new URLSearchParams({
    organizationId: String(organizationId),
    limit: String(limit),
  })
  if (normalized.direction && normalized.direction !== 'all') {
    params.set('direction', normalized.direction)
  }
  if (normalized.privateSupplierId != null) {
    params.set('privateSupplierId', String(normalized.privateSupplierId))
  }
  const result = await apiRequest<Raw[]>(`/buyer/market-analytics/price-moves?${params.toString()}`)
  return (result ?? []).map((entry) => normalizePriceMove(entry as Raw))
}

export const fetchPriceHistory = async (
  organizationId: number,
  catalogItemId: number,
  limit = 90,
): Promise<PriceHistoryPointDto[]> => {
  const params = new URLSearchParams({
    organizationId: String(organizationId),
    catalogItemId: String(catalogItemId),
    limit: String(limit),
  })
  const result = await apiRequest<Raw[]>(
    `/buyer/market-analytics/price-history?${params.toString()}`,
  )
  return (result ?? []).map((entry) => normalizeHistoryPoint(entry as Raw))
}
